import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authedFetch } from "@/lib/authed-fetch";
import { resendLatestBroadcastToSelf } from "@/lib/push.functions";

export const Route = createFileRoute("/_authenticated/admin/broadcasts/resend-me")({
  component: ResendToMePage,
});

function ResendToMePage() {
  const fired = useRef(false);
  const [state, setState] = useState<
    | { kind: "sending" }
    | { kind: "done"; sent: number; total: number; broadcastId: string | null }
    | { kind: "error"; message: string }
  >({ kind: "sending" });

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    resendLatestBroadcastToSelf({ data: {}, fetch: authedFetch })
      .then((res) => setState({ kind: "done", ...res }))
      .catch((e: Error) => setState({ kind: "error", message: e.message }));
  }, []);

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm">
        <Link to="/admin/broadcasts">
          <ArrowLeft className="h-4 w-4" /> Back to broadcasts
        </Link>
      </Button>

      <div className="rounded-xl border border-border/60 bg-card p-8">
        <h1 className="font-display text-2xl font-bold">Resend latest broadcast to me</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          One-shot QA tool. Pushes the most recent broadcast only to your own subscribed devices.
          No history row is written.
        </p>

        <div className="mt-6">
          {state.kind === "sending" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Sending.
            </div>
          )}
          {state.kind === "done" && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <div>
                <div className="font-bold">Sent to {state.sent} of {state.total} of your devices.</div>
                {state.broadcastId && (
                  <div className="text-xs opacity-80">Broadcast id: {state.broadcastId}</div>
                )}
                {state.total === 0 && (
                  <div className="text-xs">No enabled subscriptions on your account. Open the PWA and enable push first.</div>
                )}
              </div>
            </div>
          )}
          {state.kind === "error" && (
            <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <div>{state.message}</div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => {
              fired.current = false;
              setState({ kind: "sending" });
              resendLatestBroadcastToSelf({ data: {}, fetch: authedFetch })
                .then((res) => setState({ kind: "done", ...res }))
                .catch((e: Error) => setState({ kind: "error", message: e.message }));
            }}
            disabled={state.kind === "sending"}
          >
            Send again
          </Button>
        </div>
      </div>
    </div>
  );
}
